import React, { useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { RequiredSign } from "../../Constant/common";
import BaseInput from "../BaseInput";

/**
 * BaseTimePicker — consistent time picker using react-datepicker.
 *
 * Props:
 *   name        string   — field name
 *   label       node     — label text / JSX
 *   value       string   — "HH:mm" or "HH:mm:ss"
 *   onChange    fn(val)  — called with "HH:mm" string (or "" when cleared)
 *   onBlur      fn
 *   error       string
 *   touched     bool
 *   placeholder string
 *   required    bool
 *   disabled    bool
 *   timeIntervals number  — minutes between options (default 15)
 */
const BaseTimePicker = ({
  name,
  label,
  value,
  onChange,
  onBlur,
  error,
  touched,
  placeholder,
  required,
  disabled,
  timeIntervals = 15,
  isDuration = false,
}) => {
  const isInvalid = !!(touched && error);

  /* Convert "HH:mm" string → Date for the picker */
  const selectedTime = useMemo(() => {
    if (!value) return null;
    const parts = value.split(":");
    if (parts.length < 2) return null;
    const d = new Date();
    d.setHours(parseInt(parts[0], 10), parseInt(parts[1], 10), 0, 0);
    return isNaN(d.getTime()) ? null : d;
  }, [value]);

  const handleChange = (date) => {
    if (!date) {
      onChange("");
      return;
    }
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    onChange(`${hh}:${mm}`);
  };

  const handleDurationChange = (part, nextValue) => {
    const currentParts = (value || "00:00").split(":").map(Number);
    let hours = Number.isFinite(currentParts[0]) ? currentParts[0] : 0;
    let minutes = Number.isFinite(currentParts[1]) ? currentParts[1] : 0;

    if (part === "hours") hours = Math.min(24, Math.max(0, Number(nextValue) || 0));
    if (part === "minutes") minutes = Math.min(59, Math.max(0, Number(nextValue) || 0));

    onChange(`${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`);
  };

  return (
    <div className="mb-3">
      {label && (
        <label htmlFor={name} className="form-label">
          {label}
          {required && <span className="text-danger ms-1">{RequiredSign}</span>}
        </label>
      )}
      {isDuration ? (
        <div className="d-flex gap-2">
          <div className="flex-fill">
            <BaseInput
              className="mb-0 w-100"
              name={`${name}_hours`}
              type="number"
              label="Hours"
              min="0"
              max="24"
              step="1"
              value={value ? Number(value.split(":")[0]) : ""}
              handleChange={(event) => handleDurationChange("hours", event.target.value)}
              handleBlur={onBlur}
              disabled={disabled}
              placeholder="0"
              touched={isInvalid}
              error={error}
            />
          </div>
          <div className="flex-fill">
            <BaseInput
              className="mb-0 w-100"
              name={`${name}_minutes`}
              type="number"
              label="Minutes"
              min="0"
              max="59"
              step="1"
              value={value ? Number(value.split(":")[1]) : ""}
              handleChange={(event) => handleDurationChange("minutes", event.target.value)}
              handleBlur={onBlur}
              disabled={disabled}
              placeholder="0"
              touched={isInvalid}
              error={error}
            />
          </div>
        </div>
      ) : (
        <DatePicker
          id={name}
          name={name}
          selected={selectedTime}
          onChange={handleChange}
          onBlur={onBlur}
          disabled={disabled}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={timeIntervals}
          timeCaption="Time"
          dateFormat="hh:mm aa"
          placeholderText={placeholder || "Select time"}
          className={`form-control${isInvalid ? " is-invalid" : ""}`}
          popperProps={{ strategy: "fixed" }}
          popperPlacement="bottom-start"
          portalId="root"
          autoComplete="off"
        />
      )}
      {isInvalid && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
};

export default BaseTimePicker;
